/**
 * Which of the tz database's ids you want, for `getTimezoneLabels`.
 *
 * The label catalog names nearly every id the database has ever carried, which is more than a
 * picker should offer, so nothing is filtered by default and each of these narrows it for one
 * reason. They are independent on purpose: the ids that fail a Java service are not the same set as
 * the ids that read badly, even though the first happens to be a subset of the second today.
 */
export type TimezoneFilters = {
  /**
   * Keep only the ids whose first segment is a region, which is how the database names a place.
   *
   * Not "only regions" — `Europe/Paris` is kept and `CET` is not. Drops the ids with no region at
   * all: `CET`, `Japan`, `GMT0`, `Factory`, and the `US/`, `Canada/`, `Mexico/`, `Brazil/` and
   * `Chile/` namespaces, nine of which are spellings of UTC. Drops the `Etc/` namespace too, whose
   * `Etc/GMT±N` ids read backwards — `Etc/GMT+4` is four hours *behind* UTC.
   *
   * `Etc/UTC` is the one exception and is kept, because every picker wants UTC and none of them
   * should have to remember to add it back. So this is not quite "ids that name a place": it is the
   * regional ids, plus UTC.
   */
  regionalOnly?: boolean;
  /**
   * Keep one spelling per place, dropping the keys of `SUPERSEDED_TIMEZONES`.
   *
   * This is what stops a raw list looking duplicated: "Africa - Asmera" directly under
   * "Africa - Asmara".
   */
  currentSpellingsOnly?: boolean;
  /**
   * Drop the ids in `TIMEZONES_REJECTED_BY_JVM`.
   *
   * Pass this when the chosen value is persisted to a Java service, which will reject them.
   */
  jvmSupportedOnly?: boolean;
};

/**
 * Ids the JVM's copy of the tz database does not carry, so `ZoneId.of` throws for them.
 *
 * Saving one to a Java service fails validation: it is what made a scheduled workflow set to `EST`
 * fail to publish, with `Invalid IANA timezone: EST`. Derived by running `ZoneId.of` over every id
 * in `timezoneMessages` on Corretto 17, tzdb 2026b — nothing else in it is rejected.
 *
 * Exported as data because it is a fact about a backend rather than about a locale, so a caller
 * that has no label map in hand can still check a single stored value against it.
 */
export const TIMEZONES_REJECTED_BY_JVM: ReadonlySet<string> = new Set([
  'EST',
  'HST',
  'MST',
  'ROC',
]);

/**
 * Spellings the database keeps for compatibility, mapped to the one it prefers now.
 *
 * An id belongs here only when it names the *same* place as the id it points at — an alternate
 * spelling (`Asmera`), a rename (`Calcutta`), a re-parented id (`America/Cordoba` under
 * `America/Argentina/`), or the region containing it (`Australia/NSW`, `Pacific/Samoa`). An id that
 * names a *different* place is not superseded and stays, however small it is, because sharing
 * another place's rules is not the same as being that place: `Asia/Tel_Aviv`, `America/Shiprock`
 * and `Pacific/Enderbury` are kept for the same reason `Europe/Amsterdam` is.
 *
 * A few point at what the database still calls the canonical zone, because it has not caught up
 * with the rename — `Asia/Rangoon` is canonical and `Asia/Yangon` the alias — so following tz
 * data's own links would land on the superseded spelling instead.
 *
 * A map rather than a set so a caller can resolve as well as filter: a value stored as
 * `Asia/Calcutta` should be shown as `Asia/Kolkata`. It is sparse — `get` returns `undefined` for
 * any id that is not superseded, which is most of them, so resolve with
 * `SUPERSEDED_TIMEZONES.get(id) ?? id`.
 *
 * Every replacement is an id `timezoneMessages` carries, or the place would leave the list with
 * nothing standing in for it — which is why `Europe/Kiev` and `America/Godthab` are absent, there
 * being no `Europe/Kyiv` or `America/Nuuk` label to send them to.
 */
export const SUPERSEDED_TIMEZONES: ReadonlyMap<string, string> = new Map(
  Object.entries({
    'Africa/Asmera': 'Africa/Asmara',
    'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
    'America/Catamarca': 'America/Argentina/Catamarca',
    'America/Cordoba': 'America/Argentina/Cordoba',
    'America/Indianapolis': 'America/Indiana/Indianapolis',
    'America/Jujuy': 'America/Argentina/Jujuy',
    'America/Knox_IN': 'America/Indiana/Knox',
    'America/Louisville': 'America/Kentucky/Louisville',
    'America/Mendoza': 'America/Argentina/Mendoza',
    'America/Virgin': 'America/St_Thomas',
    'Asia/Ashkhabad': 'Asia/Ashgabat',
    'Asia/Calcutta': 'Asia/Kolkata',
    'Asia/Chungking': 'Asia/Chongqing',
    'Asia/Dacca': 'Asia/Dhaka',
    'Asia/Istanbul': 'Europe/Istanbul',
    'Asia/Katmandu': 'Asia/Kathmandu',
    'Asia/Macao': 'Asia/Macau',
    'Asia/Rangoon': 'Asia/Yangon',
    'Asia/Saigon': 'Asia/Ho_Chi_Minh',
    'Asia/Thimbu': 'Asia/Thimphu',
    'Asia/Ujung_Pandang': 'Asia/Makassar',
    'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
    'Atlantic/Faeroe': 'Atlantic/Faroe',
    'Australia/ACT': 'Australia/Sydney',
    'Australia/LHI': 'Australia/Lord_Howe',
    'Australia/NSW': 'Australia/Sydney',
    'Australia/North': 'Australia/Darwin',
    'Australia/Queensland': 'Australia/Brisbane',
    'Australia/South': 'Australia/Adelaide',
    'Australia/Tasmania': 'Australia/Hobart',
    'Australia/Victoria': 'Australia/Melbourne',
    'Australia/West': 'Australia/Perth',
    'Australia/Yancowinna': 'Australia/Broken_Hill',
    'Europe/Nicosia': 'Asia/Nicosia',
    'Pacific/Ponape': 'Pacific/Pohnpei',
    'Pacific/Samoa': 'Pacific/Pago_Pago',
    'Pacific/Truk': 'Pacific/Chuuk',
  }),
);

/** The regions the database groups places under. An id outside them names no place. */
const REGIONS: ReadonlySet<string> = new Set([
  'Africa',
  'America',
  'Antarctica',
  'Arctic',
  'Asia',
  'Atlantic',
  'Australia',
  'Europe',
  'Indian',
  'Pacific',
]);

/** The one `Etc/` id worth offering, and how the dashboard spells UTC. */
const UTC = 'Etc/UTC';

/** Whether an IANA id survives the given filters. Nothing set means everything survives. */
export default function matchesTimezoneFilters(
  id: string,
  {regionalOnly, currentSpellingsOnly, jvmSupportedOnly}: TimezoneFilters,
): boolean {
  if (
    regionalOnly &&
    !REGIONS.has(id.slice(0, id.indexOf('/'))) &&
    id !== UTC
  ) {
    return false;
  }
  if (currentSpellingsOnly && SUPERSEDED_TIMEZONES.has(id)) {
    return false;
  }
  if (jvmSupportedOnly && TIMEZONES_REJECTED_BY_JVM.has(id)) {
    return false;
  }
  return true;
}
